import { useTranslation } from "react-i18next";
import { LegalModal } from "./LegalModal";
import { LegalSection } from "./LegalSection";

export const CookiePolicy = () => {
  const { t } = useTranslation();

  return (
    <LegalModal title={t("legal.cookies.title")}>
      <div className="space-y-6">
        {/* Metadata Header */}
        <div className="mb-8 p-4 bg-muted/30 rounded-lg space-y-2 text-sm">
          <p><strong>Effective Date:</strong> To be determined (Launch Date)</p>
          <p><strong>Last Updated:</strong> 14 October 2025</p>
          <p><strong>Company:</strong> Narrate BV t/a Momentum Lab</p>
          <p><strong>Contact:</strong> <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">legal@momentumlab.earth</a></p>
        </div>

        <LegalSection title={t("legal.cookies.section1.title")}>
          <p>{t("legal.cookies.section1.content")}</p>
          <p>Cookies are small text files stored on your device when you visit our website. They help us provide a better user experience and understand how our platform is used.</p>
        </LegalSection>

        <LegalSection title={t("legal.cookies.section2.title")}>
          <p>{t("legal.cookies.section2.content")}</p>
          <p>We use several types of cookies:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Essential Cookies:</strong> Required for basic website functionality</li>
            <li><strong>Preference Cookies:</strong> Remember your settings (theme, language)</li>
            <li><strong>Analytics Cookies:</strong> Help us understand usage patterns</li>
            <li><strong>Marketing Cookies:</strong> Track campaign performance</li>
          </ul>
        </LegalSection>

        <LegalSection title="Specific Cookies Used">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse border border-border/30 rounded-lg">
              <thead className="bg-muted/50">
                <tr>
                  <th className="border border-border/30 px-4 py-2 text-left">Cookie Name</th>
                  <th className="border border-border/30 px-4 py-2 text-left">Purpose</th>
                  <th className="border border-border/30 px-4 py-2 text-left">Type</th>
                  <th className="border border-border/30 px-4 py-2 text-left">Duration</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>theme</code></td>
                  <td className="border border-border/30 px-4 py-2">Stores light/dark mode preference</td>
                  <td className="border border-border/30 px-4 py-2">Essential</td>
                  <td className="border border-border/30 px-4 py-2">1 year</td>
                </tr>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>i18nextLng</code></td>
                  <td className="border border-border/30 px-4 py-2">Stores language preference</td>
                  <td className="border border-border/30 px-4 py-2">Essential</td>
                  <td className="border border-border/30 px-4 py-2">1 year</td>
                </tr>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>ph_*</code></td>
                  <td className="border border-border/30 px-4 py-2">PostHog analytics tracking</td>
                  <td className="border border-border/30 px-4 py-2">Analytics</td>
                  <td className="border border-border/30 px-4 py-2">1 year</td>
                </tr>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>_ga</code></td>
                  <td className="border border-border/30 px-4 py-2">Google Analytics user identification</td>
                  <td className="border border-border/30 px-4 py-2">Analytics</td>
                  <td className="border border-border/30 px-4 py-2">2 years</td>
                </tr>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>_gid</code></td>
                  <td className="border border-border/30 px-4 py-2">Google Analytics session tracking</td>
                  <td className="border border-border/30 px-4 py-2">Analytics</td>
                  <td className="border border-border/30 px-4 py-2">24 hours</td>
                </tr>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>__hssc</code></td>
                  <td className="border border-border/30 px-4 py-2">HubSpot session tracking</td>
                  <td className="border border-border/30 px-4 py-2">Marketing</td>
                  <td className="border border-border/30 px-4 py-2">30 minutes</td>
                </tr>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>__hssrc</code></td>
                  <td className="border border-border/30 px-4 py-2">HubSpot session identifier</td>
                  <td className="border border-border/30 px-4 py-2">Marketing</td>
                  <td className="border border-border/30 px-4 py-2">Session</td>
                </tr>
                <tr>
                  <td className="border border-border/30 px-4 py-2"><code>__hstc</code></td>
                  <td className="border border-border/30 px-4 py-2">HubSpot visitor tracking</td>
                  <td className="border border-border/30 px-4 py-2">Marketing</td>
                  <td className="border border-border/30 px-4 py-2">13 months</td>
                </tr>
              </tbody>
            </table>
          </div>
        </LegalSection>

        <LegalSection title="Third-Party Cookies">
          <p>We use third-party services that may set their own cookies:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>PostHog:</strong> Product analytics and behaviour tracking</li>
            <li><strong>Google Tag Manager & Analytics:</strong> Website analytics and tag management</li>
            <li><strong>HubSpot:</strong> Marketing automation and CRM</li>
            <li><strong>Sentry:</strong> Error tracking and performance monitoring</li>
          </ul>
          <p>These third parties have their own privacy policies and cookie policies.</p>
        </LegalSection>

        <LegalSection title={t("legal.cookies.section3.title")}>
          <p>{t("legal.cookies.section3.content")}</p>
          <p>You can control cookies through:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Browser Settings:</strong> Most browsers allow you to refuse or delete cookies</li>
            <li><strong>Opt-Out Tools:</strong> Use browser extensions like Privacy Badger or uBlock Origin</li>
            <li><strong>Do Not Track:</strong> Enable DNT in your browser settings</li>
            <li><strong>Third-Party Opt-Outs:</strong> Visit Google's ad settings and HubSpot's privacy centre</li>
          </ul>
          <p className="mt-3 text-sm italic opacity-80">Note: Blocking essential cookies may affect website functionality and user experience.</p>
        </LegalSection>

        <LegalSection title="Cookie Retention Periods">
          <p>Different cookies have different retention periods:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li><strong>Session cookies:</strong> Deleted when you close your browser</li>
            <li><strong>Persistent cookies:</strong> Remain for a set period (see table above)</li>
            <li><strong>Analytics cookies:</strong> Typically 1-2 years</li>
            <li><strong>Marketing cookies:</strong> Up to 13 months</li>
          </ul>
        </LegalSection>

        <LegalSection title="Updates to This Policy">
          <p>We may update this Cookie Policy from time to time. Any changes will be posted on this page with an updated "Last Updated" date. We encourage you to review this policy periodically.</p>
        </LegalSection>

        {/* Contact Footer */}
        <div className="mt-8 pt-6 border-t border-border text-center">
          <p className="text-foreground/70 tracking-wide">
            Questions about cookies? Contact us at{' '}
            <a href="mailto:legal@momentumlab.earth" className="text-brand-teal hover:underline">
              legal@momentumlab.earth
            </a>
          </p>
        </div>
      </div>
    </LegalModal>
  );
};
